import NextLink from 'next/link';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import Image from '@alltrails/core/components/Image';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import { usePOIProfilePhoto } from '@alltrails/explore-service/hooks/usePOIProfilePhoto';
import type { ExploreServiceSearchResult } from '@alltrails/explore-service/types/ExploreServiceResponse';
import type { POISearchResult } from '@alltrails/types/AlgoliaResults';
import { useLogExploreCardViewed } from '@/utils/analyticsHelpers';
import styles from './POIResultCard.module.scss';

type POIResultCardProps = {
  href: string;
  index: number;
  onClick?: (poi: POISearchResult) => void;
  onMouseEnter?: (result: ExploreServiceSearchResult) => void;
  onMouseLeave?: () => void;
  poi: POISearchResult;
};

const POIResultCard = ({ href, index, onClick, onMouseEnter, onMouseLeave, poi }: POIResultCardProps) => {
  const container = useRef<HTMLAnchorElement>(null);
  const isVisible = useIsVisible(container);
  const { ID, location_label, name, trails_count } = poi;
  const profilePhotoUrl = usePOIProfilePhoto({ poiId: ID, width: 810, height: 608 });
  const logExploreCardViewed = useLogExploreCardViewed('poi');

  useEffect(() => {
    if (isVisible) {
      logExploreCardViewed(ID, index);
    }
    // Only want to log this when the card becomes visible
  }, [isVisible]);

  return (
    <NextLink
      className={styles.container}
      href={href}
      onClick={() => onClick?.(poi)}
      onMouseEnter={() => onMouseEnter?.(poi)}
      onMouseLeave={onMouseLeave}
      ref={container}
      prefetch={false}
    >
      <Image alt={name} className={styles.profileImage} fill src={profilePhotoUrl} />
      <div className={styles.shim} />
      <div className={styles.name}>{name}</div>
      <div className={styles.details}>
        <FormattedMessage defaultMessage="{trailCount, plural, one {# trail} other {# trails}}" values={{ trailCount: trails_count }} /> ·{' '}
        {location_label}
      </div>
    </NextLink>
  );
};

export default POIResultCard;
